import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { zendeskZendeskPendoAuth } from '@crossnokaye/typescript-sdk/funcs/zendeskZendeskPendoAuth.js';
import {
  ZendeskPendoAuthRequest,
  ZendeskPendoAuthResponse,
} from '@crossnokaye/typescript-sdk/models/operations';

import { useAppConfig } from '@/hooks/use-app-config';
import { useAtlasClient } from '@/hooks/use-atlas-client';
import { useAuth } from '@/hooks/use-auth';
import { useCurrentFacility } from '@/hooks/use-current-facility';

// Constants
const PENDO_API_KEY = '52c462a9-8e98-486a-4fc2-4c47f5d2edc6';
const ZENDESK_KEY = 'e909581e-96c7-4abe-97bb-24f7df2dbe06';
const ZENDESK_TOKEN_CACHE_KEY = 'zendesk_pendo_token';
const ZENDESK_TOKEN_FACILITY_KEY = 'zendesk_pendo_facility';
const TOKEN_CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

interface ZendeskTokenCache {
  token: string;
  timestamp: number;
  facilityKey: string;
}

/**
 * Initializes Pendo analytics
 */
const initPendo = async (userEmail: string): Promise<void> => {
  try {
    await new Promise<void>((resolve, reject) => {
      // Check if Pendo is already loaded
      if ((window as any).pendo) {
        resolve();
        return;
      }

      // Pendo initialization script
      (function (apiKey) {
        (function (p: any, e: any, n: string, d: string) {
          let w: any, x: any;
          const v: any = ['initialize', 'identify', 'updateOptions', 'pageLoad', 'track'];
          const o = (p[d] = p[d] || {});
          o._q = o._q || [];
          for (w = 0, x = v.length; w < x; ++w)
            (function (m) {
              o[m] =
                o[m] ||
                function (...args: any[]) {
                  o._q[m === v[0] ? 'unshift' : 'push']([m].concat(args));
                };
            })(v[w]);
          const y = e.createElement(n);
          y.async = true;
          y.src = 'https://cdn.pendo.io/agent/static/' + apiKey + '/pendo.js';
          y.onload = () => resolve();
          y.onerror = (err: any) => reject(err);
          const z = e.getElementsByTagName(n)[0];
          z.parentNode.insertBefore(y, z);
        })(window, document, 'script', 'pendo');
      })(PENDO_API_KEY);
    });
    // Initialize Pendo with user context
    (window as any).pendo.initialize({
      visitor: {
        id: userEmail,
        email: userEmail,
      },
      account: {
        id: userEmail,
      },
    });
    console.debug('[Pendo] Initialized successfully');
  } catch (err_1) {
    console.error('[Pendo] Failed to initialize:', err_1);
  }
};

/**
 * Sets up Zendesk API compatibility layer
 * Handles both Classic Web Widget and Messaging Web Widget APIs
 */
const setupZendeskCompatibility = () => {
  // Ensure zE queue exists for early API calls
  (window as any).zE =
    (window as any).zE ||
    function (...args: any[]) {
      ((window as any).zE.q = (window as any).zE.q || []).push(args);
    };

  // Add compatibility layer after widget loads
  const originalZE = (window as any).zE;
  if (typeof originalZE === 'function') {
    (window as any).zE = function (...args: any[]) {
      // Map messenger API calls to webWidget if messenger doesn't exist
      // This handles Pendo's attempts to use the newer API
      if (args[0] === 'messenger' && typeof originalZE.messenger === 'undefined') {
        console.debug('[Zendesk] Mapping messenger API call to webWidget:', args);

        // Map common messenger commands to webWidget equivalents
        const commandMappings: { [key: string]: string } = {
          hide: 'hide',
          show: 'show',
          open: 'open',
          close: 'close',
        };

        if (args[1] && commandMappings[args[1]]) {
          args[0] = 'webWidget';
        }
      }

      try {
        return originalZE.apply(this, args);
      } catch (error) {
        console.warn('[Zendesk] API call failed:', args, error);
        // Silently handle the error to prevent breaking Pendo
      }
    };

    // Copy any existing properties
    Object.keys(originalZE).forEach((key) => {
      (window as any).zE[key] = originalZE[key];
    });
  }
};

/**
 * Loads the Zendesk Web Widget
 */
const loadZendesk = (zendeskPendoToken: string): (() => void) => {
  // Set up compatibility layer immediately before loading script
  setupZendeskCompatibility();

  // Configure JWT authentication
  (window as any).zESettings = {
    webWidget: {
      authenticate: {
        jwtFn: function (callback: (token: string) => void) {
          callback(zendeskPendoToken);
        },
      },
    },
  };

  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.src = `https://static.zdassets.com/ekr/snippet.js?key=${ZENDESK_KEY}`;
  script.async = true;

  script.onload = () => {
    console.debug('[Zendesk] Web Widget loaded successfully');
    // Hide the default Zendesk widget launcher to prevent it from blocking other UI elements
    // Users should interact with Zendesk through the GraduationCap icon instead

    // Multiple approaches to ensure the widget is fully hidden
    const hideWidget = () => {
      try {
        (window as any).zE('webWidget', 'hide');

        // Additional fallback: directly hide the launcher iframe if it exists
        const launcherIframe = document.getElementById('launcher');
        if (launcherIframe) {
          launcherIframe.style.display = 'none';
          console.debug('[Zendesk] Direct iframe hiding applied');
        }

        // Hide any other potential Zendesk elements
        const zendeskElements = document.querySelectorAll(
          'iframe[src*="zdassets.com"], iframe[title*="widget"]'
        );
        zendeskElements.forEach((element: Element) => {
          if (element instanceof HTMLElement) {
            element.style.display = 'none';
          }
        });
      } catch (error) {
        console.warn('[Zendesk] Error hiding widget:', error);
      }
    };

    // Immediate hide attempt
    hideWidget();

    // Retry after a short delay to handle timing issues
    setTimeout(hideWidget, 100);
    setTimeout(hideWidget, 500);
  };

  script.onerror = () => {
    console.error('[Zendesk] Failed to load Web Widget');
  };

  document.head.appendChild(script);

  // Return cleanup function immediately (like Darkwing)
  return () => {
    const scriptElement = document.getElementById('ze-snippet');
    if (scriptElement) {
      document.head.removeChild(scriptElement);
    }
  };
};

/**
 * Custom hook to manage external scripts (Pendo and Zendesk)
 * Only loads in office and production environments
 */
export const useExternalScripts = () => {
  const { data: appConfig } = useAppConfig();
  const { user, isAuthenticated } = useAuth();
  const { currentFacility, organizationId, agentId } = useCurrentFacility();
  const atlasClient = useAtlasClient();
  const zendeskCleanupRef = useRef<(() => void) | null>(null);

  // Create a unique key for the current facility context
  const facilityKey = `${organizationId}-${agentId}-${currentFacility?.shortName}`;

  // Check if we should load external scripts
  // - Only load in office and production environments
  // - Only load in production builds
  // - Only load in non-test automation environments
  // - Only load if the user is authenticated
  // - Only load if the user has an email
  // - Only load if the organization ID is set
  // - Only load if the agent ID is set
  // - Only load if the facility short name is set
  const shouldLoadScripts =
    appConfig &&
    ['office', 'production'].includes(appConfig.deployment.environment) &&
    import.meta.env.PROD &&
    !navigator.webdriver &&
    isAuthenticated &&
    user?.email &&
    organizationId &&
    agentId &&
    currentFacility?.shortName;

  // Query for Zendesk token with caching
  const { data: zendeskToken } = useQuery({
    queryKey: ['zendesk-pendo-token', facilityKey],
    meta: {
      toast: {
        severity: 'off',
      },
    },
    queryFn: async () => {
      // Check cache first
      const cachedData = sessionStorage.getItem(ZENDESK_TOKEN_CACHE_KEY);
      const cachedFacility = sessionStorage.getItem(ZENDESK_TOKEN_FACILITY_KEY);

      if (cachedData && cachedFacility === facilityKey) {
        try {
          const cache: ZendeskTokenCache = JSON.parse(cachedData);
          const age = Date.now() - cache.timestamp;

          // Use cache if it's still valid and for the same facility
          if (age < TOKEN_CACHE_DURATION && cache.facilityKey === facilityKey) {
            console.debug('[Zendesk] Using cached token');
            return cache.token;
          }
        } catch (e) {
          console.warn('[Zendesk] Failed to parse cached token');
        }
      }

      // Fetch new token
      console.debug('[Zendesk] Fetching new token');
      const result = await atlasClient<ZendeskPendoAuthRequest, ZendeskPendoAuthResponse>({
        atlasFunc: zendeskZendeskPendoAuth,
        request: {
          facilityName: currentFacility!.shortName,
          orgId: organizationId!,
          agentId: agentId!,
        },
      });

      // Cache the token
      const cacheData: ZendeskTokenCache = {
        token: result.string || '',
        timestamp: Date.now(),
        facilityKey,
      };
      sessionStorage.setItem(ZENDESK_TOKEN_CACHE_KEY, JSON.stringify(cacheData));
      sessionStorage.setItem(ZENDESK_TOKEN_FACILITY_KEY, facilityKey);

      return result.string || '';
    },
    enabled: !!shouldLoadScripts,
    staleTime: TOKEN_CACHE_DURATION,
    gcTime: TOKEN_CACHE_DURATION,
  });

  // Initialize external scripts (Pendo first, then Zendesk - matching Darkwing's order)
  useEffect(() => {
    if (!shouldLoadScripts || !user?.email || !zendeskToken) {
      if (shouldLoadScripts && user?.email && !zendeskToken) {
        console.debug('[External Scripts] Waiting for Zendesk token...');
      }
      return;
    }

    let zendeskCleanup: (() => void) | undefined;

    const initializeScripts = async () => {
      try {
        // Initialize Pendo first (like Darkwing)
        console.debug('[External Scripts] Initializing Pendo');
        if (user.email) {
          await initPendo(user.email);
        }

        // Then load Zendesk with JWT auth
        console.debug('[External Scripts] Initializing Zendesk');
        zendeskCleanup = loadZendesk(zendeskToken);
        zendeskCleanupRef.current = zendeskCleanup;
      } catch (error) {
        console.error('[External Scripts] Error during initialization:', error);
      }
    };

    initializeScripts();

    return () => {
      if (zendeskCleanup) {
        zendeskCleanup();
        zendeskCleanupRef.current = null;
      }
    };
  }, [shouldLoadScripts, user?.email, zendeskToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (zendeskCleanupRef.current) {
        zendeskCleanupRef.current();
        zendeskCleanupRef.current = null;
      }
    };
  }, []);
};
